import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from "@angular/core";
import { Params, Router } from "@angular/router";
import { Organization } from "../organization/organization";
import { PersonLogin, RoleName } from "../person/person-login";
import { SelectionItem } from "../selections/selection-item";
import { OrganizationService } from "../services/organization-service";
import { PersonLoginService } from "../services/person-login-service";
import { PersonService } from "../services/person-service";
import { SelectionService } from "../services/selection-service";
import { StringUtils } from "../utils/string-utils";
import { Person } from "./person";
import { FormsModule } from "@angular/forms";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatIcon } from "@angular/material/icon";
import { MatButton } from "@angular/material/button";
import { DatePipe } from "@angular/common";
import { firstValueFrom } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";

@Component({
  selector: "person-detail",
  templateUrl: "./person-detail.component.html",
  styleUrls: ["./person-detail.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatProgressSpinner, FormsModule, DatePipe],
})
export class PersonDetailComponent implements OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly personService = inject(PersonService);
  private readonly organizationService = inject(OrganizationService);
  private readonly selectionService = inject(SelectionService);
  private readonly router = inject(Router);
  personLogin!: PersonLogin;
  readonly personId = input.required<string>({ alias: 'person-id' });
  readonly loadedPerson = signal<Person>(undefined!);
  readonly isBusy = signal(false);
  readonly errorMessage = signal<string>(undefined!);
  selectedId!: string;
  primaryOrgUnit: Organization;
  userCustomerOrgUnit: Organization;
  userVendorOrgUnit: Organization;
  controllingOrgs!: SelectionItem[];
  regions!: SelectionItem[];
  repOffices!: SelectionItem[];
  personTypes!: SelectionItem[];
  userTypes!: SelectionItem[];
  personStatusCodes!: SelectionItem[];
  availablePartsSalesManagers!: SelectionItem[];
  availableUnitSalesManagers!: SelectionItem[];

  constructor() {
    this.loadedPerson.set(new Person());
    this.primaryOrgUnit = new Organization();
    this.userCustomerOrgUnit = new Organization();
    this.userVendorOrgUnit = new Organization();
  }

  ngOnInit(): void {
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (!this.personLogin) {
      this.router.navigate(["main"]);
    } else if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(["ratings"]);
    } else {
      this.isBusy.set(true);
      this.initialize().then(
        () => (this.isBusy.set(false)),
        (error: HttpErrorResponse) => this.handleError(error),
      );
    }
  }

  private async initialize(): Promise<void> {
    const personId: string = this.personId();
    if (personId && personId.length > 0) {
      if (personId === "new") {
        if (this.personLoginService.hasRole(RoleName.IT)) {
          await this.loadSelectionLists();
          await this.loadOrganizations();
          this.startNewPerson();
        } else {
          this.backToPersonSearch();
        }
      } else {
        this.loadedPerson.set(await firstValueFrom<Person>(this.personService.getPersonById(personId)));
        if (
          this.personLoginService.hasRole(RoleName.IT) ||
          (this.loadedPerson() && this.loadedPerson().primaryOrgUnitId && this.loadedPerson().primaryOrgUnitId === this.personLogin.person.primaryOrgUnitId)
        ) {
          await this.loadSelectionLists();
          await this.loadOrganizations();
        } else {
          this.backToPersonSearch();
        }
      }
    } else {
      this.backToPersonSearch();
    }
  }

  private async loadOrganizations(): Promise<void> {
    if (!this.hasRole(RoleName.IT)) {
      this.primaryOrgUnit = await firstValueFrom<Organization>(this.organizationService.getOrganization(this.loadedPerson().primaryOrgUnitId));
      this.userCustomerOrgUnit = await firstValueFrom<Organization>(this.organizationService.getOrganization(this.loadedPerson().userCustomerOrgId));
      this.userVendorOrgUnit = await firstValueFrom<Organization>(this.organizationService.getOrganization(this.loadedPerson().userVendorOrgId));
    } else {
      this.controllingOrgs = await firstValueFrom<SelectionItem[]>(this.selectionService.getControllingOrgs());
      this.regions = await firstValueFrom<SelectionItem[]>(this.selectionService.getActiveRegionOrgs());
      this.repOffices = await firstValueFrom<SelectionItem[]>(this.selectionService.getActiveRepOffices());
    }
  }

  public hasRole(roleName: string) {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  public isNewPerson() {
    return !this.loadedPerson() || !this.loadedPerson().personId || this.loadedPerson().personId.length === 0;
  }

  public isRolesMaintainable() {
    return this.loadedPerson() && this.loadedPerson().personId && this.loadedPerson().personId.length > 0;
  }

  public backToPersonSearch(): void {
    this.router.navigate(["person"]);
  }

  private async loadSelectionLists(): Promise<void> {
    this.personTypes = await firstValueFrom<SelectionItem[]>(this.selectionService.getPersonTypes());
    this.userTypes = await firstValueFrom<SelectionItem[]>(this.selectionService.getUserTypes());
    this.personStatusCodes = await firstValueFrom<SelectionItem[]>(this.selectionService.getPersonStatusCodes());
    let userList: SelectionItem[] = await firstValueFrom<SelectionItem[]>(this.selectionService.getSalesManagers());
    userList = userList.filter((user) => StringUtils.isNotBlank(user.description)).sort((first, second) => first.description.localeCompare(second.description));
    this.availablePartsSalesManagers = JSON.parse(JSON.stringify(userList));
    this.availableUnitSalesManagers = JSON.parse(JSON.stringify(userList));
  }

  private startNewPerson() {
    this.loadedPerson().active = true;
    this.loadedPerson().statusCode = "ACTV";
  }

  public manageRoles() {
    this.router.navigate(["person/roles/" + this.loadedPerson().personId]);
  }

  public savePerson(): void {
    if (!this.loadedPerson().primaryOrgUnitId || this.loadedPerson().primaryOrgUnitId.length === 0) {
      this.errorMessage.set("Please select a Primary Org");
    } else if (!this.loadedPerson().userCustomerOrgId || this.loadedPerson().userCustomerOrgId.length === 0) {
      this.errorMessage.set("Please select a Region");
    } else if (!this.loadedPerson().userVendorOrgId || this.loadedPerson().userVendorOrgId.length === 0) {
      this.errorMessage.set("Please select a Parent Office");
    } else if (!this.loadedPerson().firstName || this.loadedPerson().firstName.length === 0) {
      this.errorMessage.set("Please enter a first name");
    } else if (!this.loadedPerson().lastName || this.loadedPerson().lastName.length === 0) {
      this.errorMessage.set("Please enter a last name");
    } else {
      this.isBusy.set(true);
      this.errorMessage.set(null!);
      if (this.isNewPerson()) {
        this.loadedPerson().createUser = this.personLogin.person.userLogin;
        this.loadedPerson().createTimestamp = new Date();
      }
      if (!this.isRegionalSalesManagerType()) {
        this.loadedPerson().supervisorId = null!;
        this.loadedPerson().partSalesManagerId = null!;
      }
      this.loadedPerson().updateUser = this.personLogin.person.userLogin;
      this.loadedPerson().updateTimestamp = new Date();
      this.loadedPerson().description = this.loadedPerson().firstName + " " + this.loadedPerson().lastName;
      this.personService.save(this.loadedPerson()).subscribe({
        next: (result: Person) => {
          this.isBusy.set(false);
          this.loadedPerson.set(result);
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
    }
  }

  isRegionalSalesManagerType(): boolean {
    return StringUtils.equalsIgnoreCase("REGMGR", this.loadedPerson().typeCode);
  }

  private handleError(error: HttpErrorResponse) {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  salesManagerInputDisabled(): boolean {
    return this.isBusy() || !this.isRegionalSalesManagerType() || !this.personLoginService.isAdmin();
  }
}
